import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { addEvCustomer, getEvCustomer, getEvLoans, updateEvCustomer } from "../../../apis/evLoans";
import EvCustomerForm, { blankEvCustomer, customerDocuments, evCustomerForEdit } from "./EvCustomerForm";
import { parseCustomerAddress } from "./customerAddress";
import { formatEvMoney } from "./evLoanFormatting";
import "./ev-loans.css";

const messageOf = (error) => error?.response?.data?.message || error.message || "Request failed";
const money = formatEvMoney;
const account = (loan) => loan.loan_account_number || `VasudevEV${String(loan.id).padStart(6, "0")}`;
function details(value) {
  if (typeof value !== "string") return value || {};
  try { return JSON.parse(value) || {}; } catch { return {}; }
}
function Info({ label, value }) { return <div className="col-md-4"><small className="text-muted d-block">{label}</small><strong>{value || "—"}</strong></div>; }

export default function EvCustomerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const creating = !id;
  const [customer, setCustomer] = useState(null);
  const [form, setForm] = useState(blankEvCustomer);
  const [loans, setLoans] = useState([]);
  const [editing, setEditing] = useState(creating);
  const [loading, setLoading] = useState(!creating);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (creating) return;
    setLoading(true);
    Promise.all([getEvCustomer(id), getEvLoans()]).then(([result, loanResult]) => {
      setCustomer(result.data);
      setForm(evCustomerForEdit(result.data));
      setLoans((loanResult.data || []).filter((loan) => String(loan.customer_id) === String(id)));
    }).catch((failure) => setError(messageOf(failure))).finally(() => setLoading(false));
  }, [id, creating]);

  async function submit(event) {
    event.preventDefault(); setBusy(true); setError("");
    try {
      const result = creating ? await addEvCustomer(form) : await updateEvCustomer(id, form);
      if (creating) navigate(all_routes.evCustomers, { replace: true });
      else { setCustomer(result.data); setForm(evCustomerForEdit(result.data)); setEditing(false); setMessage("Customer updated"); }
    } catch (failure) { setError(messageOf(failure)); }
    finally { setBusy(false); }
  }

  const address = parseCustomerAddress(customer?.address);
  const permanent = parseCustomerAddress(customer?.current_address);
  const guarantor = details(customer?.guarantor);
  return <div className="page-wrapper modern-ui"><div className="content container-fluid">
    <Link className="btn btn-link ps-0 mb-2" to={all_routes.evCustomers}>← EV customers</Link>
    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
      <div><h3 className="mb-1">{creating ? "Add EV customer" : customer?.full_name || "EV customer details"}</h3>
        {!creating && <span className="text-muted">{customer?.loanAccountNumber}</span>}</div>
      {!creating && customer && <div className="d-flex gap-2"><button className="btn btn-outline-primary" onClick={() => setEditing((old) => !old)}>{editing ? "View details" : "Edit customer"}</button>
        <Link className="btn btn-primary" to={`${all_routes.evCustomerLoans}?customer_id=${id}`}>Apply loan</Link></div>}
    </div>
    {loading && <p role="status">Loading customer...</p>}
    {error && <div className="alert alert-danger" role="alert">{error}</div>}
    {message && <div className="alert alert-success" role="status">{message}</div>}
    {editing && <div className="card mb-4"><div className="card-header">{creating ? "Customer information" : "Edit customer information"}</div>
      <div className="card-body"><EvCustomerForm customer={form} setCustomer={setForm} onSubmit={submit} busy={busy || uploading} editing={!creating}
        onUploadingChange={setUploading} onCancel={() => creating ? navigate(all_routes.evCustomers) : setEditing(false)} /></div></div>}
    {!creating && customer && !editing && <>
      <div className="card mb-4"><div className="card-header">Customer details</div><div className="card-body row g-3">
        <Info label="Name" value={customer.full_name} /><Info label="Phone" value={customer.phoneNumber1} /><Info label="Alternate phone" value={customer.phoneNumber2} />
        <Info label="Email" value={customer.email} /><Info label="Father's name" value={customer.fatherName} /><Info label="Date of birth" value={customer.dob && String(customer.dob).slice(0, 10)} />
        <Info label="Aadhaar" value={customer.aadharNumber} /><Info label="PAN" value={customer.PanNumber} /><Info label="Occupation" value={customer.occupation} />
        <Info label="Monthly income" value={customer.income && money(customer.income)} /><Info label="Monthly expenses" value={customer.monthlyExpenses && money(customer.monthlyExpenses)} />
        <Info label="Current address" value={Object.values(address).filter(Boolean).join(", ")} /><Info label="Permanent address" value={Object.values(permanent).filter(Boolean).join(", ")} />
      </div></div>
      <div className="card mb-4"><div className="card-header">Bank details</div><div className="card-body row g-3">
        <Info label="Bank" value={customer.bankName} /><Info label="Account number" value={customer.bankAccountNo} /><Info label="IFSC" value={customer.ifscCode} />
      </div></div>
      <div className="card mb-4"><div className="card-header">Guarantor details</div><div className="card-body row g-4">
        {["first", "second"].map((which, index) => <div className="col-md-6" key={which}><h5>Guarantor {index + 1}</h5><div className="row g-3">
          <Info label="Name" value={guarantor[which]?.name} /><Info label="Phone" value={guarantor[which]?.mobile} /><Info label="Aadhaar" value={guarantor[which]?.aadhar_number} />
          <Info label="Email" value={guarantor[which]?.email} /><Info label="Address" value={guarantor[which]?.address} />
          {["photo", "aadharFront", "aadharBack", "panCardImage"].map((key) => guarantor[which]?.[key] && <div className="col-auto" key={key}><a target="_blank" rel="noopener noreferrer" href={guarantor[which][key]}>{key}</a></div>)}
        </div></div>)}
      </div></div>
      <div className="card mb-4"><div className="card-header">Documents</div><div className="card-body d-flex flex-wrap gap-3">
        {customerDocuments.map(([key, label]) => <span key={key}>{customer[key] ? <a href={customer[key]} target="_blank" rel="noopener noreferrer">{label}</a> : <span className="text-muted">{label}: —</span>}</span>)}
      </div></div>
      <div className="card"><div className="card-header d-flex justify-content-between align-items-center"><span>Customer EV loans</span>
        <Link className="btn btn-sm btn-primary" to={`${all_routes.evCustomerLoans}?customer_id=${id}`}>Apply loan</Link></div>
        <div className="card-body table-responsive"><table className="table"><thead><tr><th>Loan account</th><th>Product</th><th>Asset</th><th>Principal</th><th>EMI</th><th>Status</th><th>Actions</th></tr></thead><tbody>
          {loans.map((loan) => <tr key={loan.id}><td>{account(loan)}</td><td>{loan.product?.name}</td><td>{loan.asset_description}</td><td>{money(loan.principal)}</td><td>{money(loan.monthly_emi)}</td><td>{loan.status}</td><td>
            <Link className="btn btn-sm btn-outline-primary me-1" to={all_routes.evLoanRecord.replace(":id", loan.id)}>EMI record</Link>
            {["approved", "disbursed", "closed"].includes(loan.status) && <Link className="btn btn-sm btn-outline-primary me-1"
              to={`${all_routes.evLoanRecord.replace(":id", loan.id)}?sanction=1`}>Sanction letter</Link>}
            {loan.status === "approved" && <Link className="btn btn-sm btn-primary" to={all_routes.evLoanDisbursement.replace(":id", loan.id)}>Disburse</Link>}
          </td></tr>)}
        </tbody></table>{!loans.length && <p className="text-muted mb-0">No EV loans yet.</p>}</div></div>
    </>}
  </div></div>;
}
